import * as React from 'react';
import { useTheme } from '../../../theme';
import cn from 'classnames';
import { Duration } from '../../../types';
import AirplaneInfo from '../AirplaneInfo/AirplaneInfo';
import { SunSides } from '@websky/graphql';
import { getDurationString } from '../../../Results/utils';
import { MOBILE_MIN_WIDTH, TABLET_MIN_WIDTH } from '../../../utils';
import i18n from 'i18next';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import AircraftPopup from '../../../AircraftPopup';
import Link from '../../../Link';
import { Aircraft, Segment } from '../../types';
import { useCallback } from 'react';
import { InfoDetails } from '../../../Icons';
import Amenities from '../../../Amenities';
import AircraftLivery from '../../../AircraftLivery';

interface Props {
	aircraft: Aircraft;
	routeTime?: Duration;
	sunInfo?: SunSides;
	onSeatsDetailsClick?: () => void;
	segment?: Segment;
	className?: string;
	isReturnFlight?: boolean;
}

const Airplane: React.FunctionComponent<Props> = ({
	aircraft,
	sunInfo,
	routeTime,
	onSeatsDetailsClick,
	segment,
	className,
	isReturnFlight
}) => {
	const theme = useTheme('SeatMap').AirplaneStyles;

	const duration = routeTime ? getDurationString(routeTime) : null;

	const isNoneSide = sunInfo === SunSides.NoneSide;

	const { t } = useTranslation('SeatMap');

	const labels = {
		'': '',
		[SunSides.LeftSide]: i18n.t('SeatMap:90% on the left side'),
		[SunSides.RightSide]: i18n.t('SeatMap:90% on the right side')
	};

	const renderButton = useCallback(
		(onClick: () => void) => (
			<div className={theme.airplane__name}>
				<Link action={onClick}>{aircraft.name}</Link>
			</div>
		),
		[aircraft]
	);

	return (
		<div
			className={cn(theme.airplane, className, {
				[theme.airplane_noInfo]: !aircraft?.name && !aircraft?.icon
			})}
		>
			{segment && (
				<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
					<div className={theme.segment}>
						{segment.segmentInfo.departure.airport.city.name}
						{' — '}
						{segment.segmentInfo.arrival.airport.city.name}
					</div>
				</MediaQuery>
			)}

			<div className={theme.airplane__info}>
				<AircraftLivery aircraft={aircraft} className={theme.airplane__image} isReturnFlight={isReturnFlight} />

				<div className={theme.airplane__info_wrapper}>
					{aircraft && (
						<AircraftPopup
							id={aircraft.id}
							operatingIata={segment?.segmentInfo?.operatingAirline?.iata}
							name={aircraft.name}
							renderButton={renderButton}
						/>
					)}

					{segment?.amenities?.length > 0 && (
						<Amenities className={theme.airplane__amenities} amenities={segment.amenities} />
					)}
				</div>
			</div>

			<div
				className={cn(theme.details, {
					[theme.details_one]: isNoneSide
				})}
			>
				<AirplaneInfo text={duration} type="time" />

				{!isNoneSide && <AirplaneInfo text={labels[sunInfo]} type="sun" />}
			</div>

			<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
				<div className={theme.seatsDetails} onClick={onSeatsDetailsClick}>
					<div className={theme.seatsDetails_icon}>{InfoDetails}</div>
					{t('Seat details')}
				</div>
			</MediaQuery>
		</div>
	);
};

export default Airplane;
